import { LocalStorageService } from './../services/localStorage/localStorage.service';
import { Injectable } from '@angular/core';
import {
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpInterceptor,
} from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, takeLast, switchMap, filter, take, finalize } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { NavigationService } from '../services/navigation/navigation.service';
import { HttpStatusCodeConstant } from '../constants/http-status-code';
import { urlRoutes } from '../constants/url-route.constant';
import { CookieService } from '../services/cookie/cookie.service';
import { AuthenticationService } from '../services/auth/authentication.service';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
     
    private isRefreshing = false;
    private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    
    constructor(
        private readonly storageService: LocalStorageService,
        private readonly toastrService: ToastrService,
        private readonly navigationService: NavigationService,
        private authService: AuthenticationService,
        private cookieService: CookieService
    ) {}

    intercept(
        request: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(
            catchError((error) => {
                let errorMessage =
                    error.error?.message || 'Oops!, Something Went Wrong!';
                    
                if (
                    (error.status === HttpStatusCodeConstant.unauthorized ||
                    error.status === HttpStatusCodeConstant.forbidden)
                ) {
                    return this.handleAuthError(request, next, error);
                } else if (error.status !== 0) {
                    if (
                        !request.url.includes('application?applicationId=') &&
                        !request.url.includes('adminExport') &&
                        !request.url.includes('userFileMaster/upload')
                    ) {
                        this.toastrService.error(
                            errorMessage
                        );
                    }
                } else {
                    this.toastrService.error(errorMessage, '', {
                        closeButton: true,
                        enableHtml: true,
                        disableTimeOut: true,
                    });
                }

                return throwError(() => error);
            })
        );
    }
    
    private handleAuthError(request: HttpRequest<any>, next: HttpHandler, error: any): Observable<HttpEvent<any>> {
        if (!this.isRefreshing) {
            this.isRefreshing = true;
            this.refreshTokenSubject.next(null);
            
            // Cache the refresh token before starting the refresh process
            const refreshToken = this.cookieService.getCookie('ref_token');
            if (!refreshToken) {
                this.isRefreshing = false;
                this.logout();
                return throwError(() => new Error('No refresh token available'));
            }
            
            return this.authService.getAccessToken(refreshToken).pipe(
                // Use first operator instead of takeLast to get the first emission
                // takeLast waits for completion which might cause timing issues
                take(1),
                switchMap((res: any) => {
                    if (res?.statusCode === 200) {
                        const newToken = res?.data?.accessToken;
                        this.storageService.setKey('access_token', newToken);
                        
                        // Signal to all waiting requests that token is available
                        this.refreshTokenSubject.next(newToken);
                        
                        // Retry the original request with new token
                        return next.handle(this.addTokenToRequest(request, newToken));
                    } else {
                        this.refreshTokenSubject.next(null); // Make sure waiting requests are notified
                        this.logout();
                        return throwError(() => new Error('Session expired'));
                    }
                }),
                catchError(err => {
                    this.refreshTokenSubject.next(null); // Make sure waiting requests are notified
                    this.logout();
                    return throwError(() => err);
                }),
                finalize(() => {
                    this.isRefreshing = false;
                })
            );
        } else {
            // If refresh is in progress, wait for the new token
            return this.refreshTokenSubject.pipe(
                filter(token => token !== null),
                take(1),
                switchMap(token => {
                    // Retry the original request with new token
                    return next.handle(this.addTokenToRequest(request, token));
                }),
                catchError(err => {
                    // If the retry fails, propagate the error
                    console.error('Request retry failed after token refresh:', err);
                    return throwError(() => err);
                })
            );
        }
    }
    
    private addTokenToRequest(request: HttpRequest<any>, token: string): HttpRequest<any> {
        // Make a copy of the headers to avoid mutation issues
        const headers = request.headers.keys().reduce((acc, key) => {
            acc[key] = request.headers.getAll(key);
            return acc;
        }, {});
        
        // Get the existing Authorization header
        const authHeader = request.headers.get('Authorization');
        
        // Create a new request with fresh headers to avoid any stale state
        const clonedRequest = request.clone({
            headers: request.headers.delete('Authorization')
        });
        
        // If there's no Authorization header or it contains 'Bearer', add the token
        if (!authHeader || authHeader.startsWith('Bearer ')) {
            return clonedRequest.clone({
                setHeaders: {
                    Authorization: `Bearer ${token}`
                }
            });
        }
        
        // If it's a different auth type (like Basic), leave it as is
        return clonedRequest;
    }

    logout(){
        let payload = {
            "token": this.cookieService.getCookie('ref_token'),
        }
        this.authService.logout(payload).subscribe(
            (res)=>{
            this.cookieService.deleteCookie('ref_token');
            this.storageService.clearAll();
            let activeToast = this.toastrService.warning(
                'Please re-login to continue',
                'Session Expired!',
                { timeOut: 2000 }
            );
            // activeToast.onHidden.pipe().subscribe(() => {
                this.navigationService.routeToPath('/authentication/login');
                // location.reload();
            // });
            },
            (err)=>{
                this.cookieService.deleteCookie('ref_token');
                this.storageService.clearAll();
                let activeToast = this.toastrService.warning(
                    'Please re-login to continue',
                    'Session Expired!',
                    { timeOut: 2000 }
                );
                // activeToast.onHidden.pipe().subscribe(() => {
                    this.navigationService.routeToPath('/authentication/login');
                    // location.reload();
                // });
            }
        )
        
    }
}